<template>
  <div class="psp-panel">
    <div v-if="loading" class="psp-loading">
      <van-loading type="spinner" color="#8131e7" />
    </div>

    <template v-else>
      <!-- 平台结算信息卡片 -->
      <div class="psp-info-card">
        <img class="psp-logo" src="../../assets/withdrawal_v2/platform.png" alt="" />
        <div class="psp-info-text">
          <p class="psp-info-title">{{ $t("guild_platform_settlement") }}</p>
          <p class="psp-info-sub">{{ $t("guild_settlement_weekly_process") }}</p>
        </div>
        <button type="button" class="psp-bind-btn" @click="openBindSheet">
          {{ bound ? $t("guild_modify") : $t("guild_bind") }}
        </button>
      </div>

      <div class="psp-main">
        <div class="psp-body">
          <p class="psp-section-title">{{ $t("guild_settlement_input_amount") }}</p>

          <div class="psp-input-wrap" @click="inputFocus">
            <img class="psp-coin" src="../../assets/withdrawal_v2/$.png" alt="" />
            <span ref="measureRef" class="psp-measure" aria-hidden="true">{{ displayInput }}</span>
            <input
              ref="inputRef"
              v-model="inputValue"
              type="text"
              inputmode="numeric"
              class="psp-input"
              :placeholder="$t('guild_please_enter')"
              @input="changeCoin"
              @click.stop="inputFocus"
              @blur="onInputBlur"
            />
          </div>

          <p class="psp-balance">
            {{ $t("guild_current_can_withdraw") }}
            <img src="../../assets/withdrawal_v2/$.png" alt="" />
            <span>{{ coinText }}</span>
          </p>

          <ul class="psp-gear-box">
            <li :class="{ active: gearType === 0 }" @click="changeGearType(0)">{{ $t("guild_all") }}</li>
            <li :class="{ active: gearType === 1 }" @click="changeGearType(1)">1/2</li>
            <li :class="{ active: gearType === 2 }" @click="changeGearType(2)">1/3</li>
            <li :class="{ active: gearType === 3 }" @click="changeGearType(3)">1/4</li>
          </ul>
        </div>

        <div class="psp-footer">
          <button type="button" class="psp-submit" @click="onWithdrawClick">
            {{ $t("withdraw_submit") }}
          </button>
        </div>
      </div>
    </template>

    <platform-settlement-bind-sheet
      :visible="showBindSheet"
      :down-num="downNum"
      :value="settlementForm"
      @close="showBindSheet = false"
      @bound="onSettlementBound"
    />
  </div>
</template>

<script>
/**
 * 平台结算 v2 主面板（2026-06-08）
 * 金额输入同自提现自定义档位（无对应法币）；绑定信息走 platform-settlement-bind-sheet
 */
import axios from "axios";
import { Toast, Loading } from "vant";
import "vant/lib/loading/style";
import PlatformSettlementBindSheet from "@/components/withdrawal/platform-settlement-bind-sheet.vue";
import {
  createEmptySettlementForm,
  isSettlementBound,
  loadSettlementInfo,
  saveSettlementInfo,
  validateSettlementForm,
} from "@/utils/platformSettlement";
import {
  BIND_TYPE_SETTLEMENT,
  saveBindInfo,
  loadBindInfo,
} from "@/utils/bindInfo";

export default {
  name: "platform-settlement-panel",
  components: {
    PlatformSettlementBindSheet,
    [Loading.name]: Loading,
  },
  data() {
    return {
      loading: true,
      coin: 0,
      downNum: 0,
      inputValue: "",
      gearType: 5,
      showBindSheet: false,
      settlementForm: {},
      token: "",
      uid: "",
      lang: "",
      deviceId: "",
      version: "3.0",
      pendingWithdrawAfterBind: false,
    };
  },
  computed: {
    // 可结算金额的千分位展示文本
    coinText() {
      return Number(this.coin || 0).toLocaleString("en-US");
    },
    // 输入框显示值：空则显示占位空串
    displayInput() {
      return this.inputValue === "" ? "" : this.inputValue;
    },
    // 是否已绑定结算信息
    bound() {
      return isSettlementBound(this.settlementForm);
    },
  },
  // 初始化：鉴权/语言/空表单/缓存回填/拉取结算信息
  created() {
    this.initAuth();
    this.initLang();
    this.settlementForm = createEmptySettlementForm(this.$t.bind(this));
    this.loadSavedForm();
    this.fetchSettlementInfo();
  },
  methods: {
    // 从 localStorage 初始化鉴权信息
    initAuth() {
      const raw = localStorage.getItem("data");
      if (!raw) return;
      try {
        const data = JSON.parse(raw);
        this.token = data.token;
        this.uid = String(data.uid ?? data.userId ?? "");
        this.lang = data.lang || navigator.language;
        this.deviceId = data.deviceId || "";
        this.version = data.version || "3.0";
      } catch (e) {
        /* ignore */
      }
    },
    // 初始化 i18n 语言：将浏览器语言映射到支持的语言码并同步 html lang
    initLang() {
      const lang = this.lang || navigator.language;
      const langs = String(lang).substring(0, 2);
      const map = {
        zh: lang === "zh-TW" || lang === "zh-HK" ? "tc" : "zh",
        en: "en",
        ar: "ar",
        tr: "tr",
        es: "es",
        pt: "pt",
        fr: "fr",
        id: "id",
        ms: "ms",
      };
      this.$i18n.locale = map[langs] || "en";
      document.querySelector("html").setAttribute("lang", this.$i18n.locale);
    },
    // 加载已绑定的结算表单：优先服务端 bindInfo（bindType=2, bizKey 写死 country），再兜底本地缓存
    loadSavedForm() {
      const country = (this.settlementForm.country || {}).code || "";
      // 有默认国家码时先从服务端加载已绑定结算信息
      if (country && this.token && this.uid) {
        loadBindInfo({
          bindType: BIND_TYPE_SETTLEMENT,
          bizKey: "country",
          serviceIp: this.$serviceIp,
          token: this.token,
          uid: this.uid,
          deviceId: this.deviceId,
          lang: this.lang,
        }).then((vo) => {
          const saved = (vo && vo.infoJson) || null;
          if (saved) {
            this.settlementForm = { ...this.settlementForm, ...saved };
          } else {
            this.loadSavedFormFromLocal();
          }
        });
        return;
      }
      this.loadSavedFormFromLocal();
    },
    // 本地缓存兜底（按 uid 校验）
    loadSavedFormFromLocal() {
      const saved = loadSettlementInfo(this.uid, this.$t.bind(this));
      if (saved) {
        this.settlementForm = saved;
      }
    },
    // 拉取平台结算信息：可结算金额与最小单位数
    fetchSettlementInfo() {
      if (!this.token || !this.uid) {
        this.loading = false;
        return;
      }
      this.loading = true;
      axios
        .get(
          `${this.$serviceIp}api/trade/card/tx/guildSettlementInfo?token=${this.token}&uid=${this.uid}`,
          {
            headers: {
              deviceId: this.deviceId,
              version: "3.0",
              lang: this.lang,
            },
          }
        )
        .then(({ data }) => {
          if (data.data && data.data.code === 200) {
            this.coin = data.data.data.amount;
            this.downNum = data.data.data.guildSettlementPocketNum;
          } else {
            Toast((data.data && data.data.message) || this.$t("transfer.error"));
          }
        })
        .catch(() => Toast(this.$t("transfer.error")))
        .finally(() => {
          this.loading = false;
        });
    },
    // 打开绑定半层
    openBindSheet() {
      this.showBindSheet = true;
    },
    // 绑定成功：保存表单（走服务端 bindInfo + 本地缓存兜底）；若绑定前曾请求提现则继续进入确认页
    onSettlementBound(form) {
      this.settlementForm = { ...form };
      saveSettlementInfo(this.uid, this.settlementForm);
      // 平台结算绑定信息走服务端保存（bindType=2, bizKey 写死 country）
      saveBindInfo({
        bindType: BIND_TYPE_SETTLEMENT,
        bizKey: "country",
        info: this.settlementForm,
        serviceIp: this.$serviceIp,
        token: this.token,
        uid: this.uid,
        deviceId: this.deviceId,
        lang: this.lang,
      });
      this.showBindSheet = false;
      if (this.pendingWithdrawAfterBind) {
        this.pendingWithdrawAfterBind = false;
        this.goConfirmPage();
      }
    },
    // 千分位格式化展示金额
    formatInputValue(raw) {
      if (!raw) return "";
      return Number(raw).toLocaleString("en-US");
    },
    // 去掉千分位逗号取实际数值
    getInputNumber() {
      return Number(String(this.inputValue).replace(/,/g, "")) || 0;
    },
    // 金额输入框聚焦：光标移到末尾，档位重置为自定义
    inputFocus() {
      const input = this.$refs.inputRef;
      if (!input) return;
      this.$nextTick(() => {
        input.focus();
        const len = input.value.length;
        input.setSelectionRange(len, len);
      });
      if (this.gearType !== 5) {
        this.gearType = 5;
      }
    },
    // 失焦处理（保持内容为空）
    onInputBlur() {
      if (this.inputValue === "") {
        this.inputValue = "";
      }
    },
    // 输入金额：清洗非法字符、千分位格式化，并按文本长度自适应输入框宽度
    changeCoin() {
      const raw =
        this.inputValue.toString().replace(/[^\d]/g, "").replace(/^0+/, "") || "";
      this.inputValue = this.formatInputValue(raw);
      this.$nextTick(() => {
        const span = this.$refs.measureRef;
        const input = this.$refs.inputRef;
        if (!span || !input) return;
        let width = span.offsetWidth + 20;
        if (raw.length > 5) width += 20;
        if (raw.length > 9) width += 20;
        if (raw.length > 13) width += 20;
        input.style.width = `${Math.max(width, 40)}px`;
        const len = this.inputValue.length;
        input.setSelectionRange(len, len);
      });
    },
    // 快捷档位：全部 / 1/2 / 1/3 / 1/4，再次点击无反应（不取消选择）
    changeGearType(type) {
      this.gearType = type;
      if (type === 0) {
        this.inputValue = this.formatInputValue(String(this.coin));
      } else if (type === 1) {
        this.inputValue = this.formatInputValue(String(Math.floor(this.coin / 2)));
      } else if (type === 2) {
        this.inputValue = this.formatInputValue(String(Math.floor(this.coin / 3)));
      } else if (type === 3) {
        this.inputValue = this.formatInputValue(String(Math.floor(this.coin / 4)));
      }
      this.changeCoin();
    },
    // 点击提现：先校验输入金额，再校验是否已绑定提现信息
    onWithdrawClick() {
      // 第一步：校验输入金额（必填 / 不超余额 / 不低于最小值）
      const amount = this.getInputNumber();
      if (!amount) {
        Toast(this.$t("guild_please_enter"));
        return;
      }
      if (amount > Number(this.coin)) {
        Toast(this.$t("guild_withdraw_note_2"));
        return;
      }
      if (amount < Number(this.downNum)) {
        Toast(this.$t("guild_tips_1") + this.downNum);
        return;
      }
      // 第二步：校验是否已绑定提现信息
      if (!this.bound) {
        this.pendingWithdrawAfterBind = true;
        this.showBindSheet = true;
        return;
      }
      // 已绑定：校验结算表单合法后进确认页
      const check = validateSettlementForm(this.settlementForm, this.$t.bind(this));
      if (!check.valid) {
        Toast(check.message);
        return;
      }
      this.goConfirmPage();
    },
    // 跳转平台结算确认页（flow=platform，携带金额与结算表单）
    goConfirmPage() {
      this.$router.push({
        path: "/self-withdraw-confirm",
        query: {
          flow: "platform",
          amount: String(this.getInputNumber()),
          settlement: JSON.stringify(this.settlementForm),
          downNum: String(this.downNum),
        },
      });
    },
  },
};
</script>

<style scoped>
.psp-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  font-family: "Montserrat", sans-serif;
  padding-top: 20px;
  box-sizing: border-box;
}

.psp-loading {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.psp-info-card {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 24px 16px 24px 24px;
  background: #fff;
  border-radius: 24px;
  width: calc(100% - 56px);
  max-width: 594px;
  box-sizing: border-box;
  margin: 0 auto;
}

.psp-logo {
  width: 120px;
  height: 68px;
  border-radius: 12px;
  object-fit: contain;
  background: #f5f6f8;
  flex-shrink: 0;
  margin-right: 24px;
}

.psp-info-text {
  flex: 1;
  min-width: 0;
}

.psp-info-title {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 600;
  color: #262427;
}

.psp-info-sub {
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  color: #89848d;
}

.psp-bind-btn {
  flex-shrink: 0;
  height: 56px;
  padding: 0 30px;
  border: none;
  border-radius: 28px;
  background: #ECDCFF;
  color: #7b4dff;
  font-size: 20px;
  font-weight: 500;
  font-family: 'Montserrat';
}

.psp-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
  background: #fff;
  border-radius: 24px 24px 0 0;
  padding: 24px 32px calc(16px + env(safe-area-inset-bottom));
  padding-bottom: calc(16px + constant(safe-area-inset-bottom));
  box-sizing: border-box;
  overflow: hidden;
}

.psp-body {
  flex: 1;
  min-height: 0;
}

.psp-section-title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 400;
  color: #89848d;
}

.psp-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 88px;
  padding: 0 24px;
  border: 1px solid #dbdbdb;
  border-radius: 24px;
  background: #fff;
  box-sizing: border-box;
}

.psp-coin {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.psp-measure {
  position: absolute;
  visibility: hidden;
  white-space: pre;
  font-size: 28px;
  font-family: "Mada", sans-serif;
  top: -9999px;
  left: -9999px;
}

.psp-input {
  flex: 1;
  min-width: 40px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 24px;
  font-weight: 500;
  color: #262427;
}

.psp-input::placeholder {
  color: #c4c0c8;
  font-weight: 400;
}

.psp-balance {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 24px 0 10px;
  font-size: 24px;
  color: #262427;
  font-weight: 400;
}

.psp-balance img {
  width: 36px;
  height: 36px;
}

.psp-balance span {
  font-size: 26px;
  /* font-weight: 700; */
}

.psp-gear-box {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.psp-gear-box li {
  height: 64px;
  border: 1px solid #dbdbdb;
  border-radius: 32px;
  font-size: 24px;
  line-height: 64px;
  padding: 0 46px;
  color: #89848d;
  text-align: center;
}

.psp-gear-box li.active {
  color: #8131e7;
  border-color: #8131e7;
}

.psp-footer {
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 48px;
  background: #fff;
  display: flex;
  justify-content: center;
}

.psp-submit {
  width: 100%;
  max-width: 412px;
  height: 80px;
  border: none;
  border-radius: 40px;
  background: #8131e7;
  font-size: 24px;
  font-weight: 500;
  color: #fff;
}

html[lang="ar"] .psp-info-card,
html[lang="ar"] .psp-main {
  direction: rtl;
}

html[lang="ar"] .psp-select::after {
  transform: scaleX(-1);
}
html[lang="ar"] .psp-logo{
  margin-right: 0;
  margin-left: 24px;
}
</style>
